import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SupportChat({ isAdmin = false, adminUserId = null }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchThread = useCallback(async () => {
    try {
      const url = isAdmin && adminUserId
        ? `${API}/admin/support/thread/${adminUserId}`
        : `${API}/support/thread`;
      const res = await axios.get(url, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch {
      // Thread may not exist yet
    }
  }, [isAdmin, adminUserId]);

  const fetchUnread = useCallback(async () => {
    try {
      const url = isAdmin ? `${API}/admin/support/unread` : `${API}/support/unread`;
      const res = await axios.get(url, { withCredentials: true });
      setUnread(res.data.unread || 0);
    } catch {
      // silent
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchThread().finally(() => setLoading(false));
      // Mark as read
      if (!isAdmin) {
        axios.post(`${API}/support/mark-read`, {}, { withCredentials: true }).catch(() => {});
      }
      setUnread(0);
      // Poll while open
      pollRef.current = setInterval(fetchThread, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, fetchThread, isAdmin]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const url = isAdmin && adminUserId
        ? `${API}/admin/support/thread/${adminUserId}/reply`
        : `${API}/support/message`;
      await axios.post(url, { message: input.trim() }, { withCredentials: true });
      setInput('');
      await fetchThread();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Trigger — icon button for top bar */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
        data-testid="support-chat-toggle"
        title="Support"
      >
        <MessageSquare className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1" data-testid="support-unread-badge">
            {unread}
          </span>
        )}
      </button>

      {/* Chat Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 20, y: -10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 20, y: -10 }}
            className="fixed top-14 right-4 z-[60] w-[360px] max-h-[500px] flex flex-col bg-slate-950 border border-blue-500/20 rounded-lg shadow-2xl shadow-blue-500/5 overflow-hidden"
            data-testid="support-chat-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-blue-500/5">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-mono font-bold text-blue-400">
                  {isAdmin ? 'Admin Support' : 'Support Chat'}
                </span>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300" data-testid="support-chat-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[250px] max-h-[350px]">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-8 w-8 text-slate-700 mx-auto mb-2" />
                  <p className="text-slate-500 text-xs">No messages yet. Start a conversation!</p>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-lg text-xs ${
                        msg.sender === 'admin'
                          ? 'bg-blue-500/15 text-blue-200 border border-blue-500/20'
                          : 'bg-slate-800 text-slate-200 border border-white/5'
                      }`}
                      data-testid={`support-msg-${i}`}
                    >
                      <p className="text-[10px] font-mono text-slate-500 mb-1">
                        {msg.sender_name} — {new Date(msg.sent_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="whitespace-pre-wrap">{msg.text}</p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/5">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 bg-slate-900/80 border border-white/10 text-slate-200 text-xs rounded-lg px-3 py-2 focus:border-blue-400/50 focus:outline-none placeholder:text-slate-600"
                  disabled={sending}
                  data-testid="support-chat-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={sending || !input.trim()}
                  size="sm"
                  className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 px-3"
                  data-testid="support-chat-send"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
